import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { useParams } from 'react-router-dom';
import { Bot as BotIcon, Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { api } from '@/lib/api';
import { Empty } from '@/components/Empty';
import { cn } from '@/lib/cn';

export function AgentsPage() {
  const { slug } = useParams<{ slug: string }>();
  const { getToken } = useAuth();

  const agentsQuery = useQuery({
    queryKey: ['agents', slug],
    queryFn: () => api.listAgents(slug!, () => getToken()),
    refetchInterval: 30_000,
  });

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold leading-tight">État des agents</h1>
        <p className="text-sm text-ducens-muted mt-1">
          Activité, santé et taux de succès sur les dernières 24 heures.
        </p>
      </div>

      {agentsQuery.isPending && (
        <div className="flex items-center gap-2 text-ducens-muted">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
        </div>
      )}

      {agentsQuery.data && agentsQuery.data.length === 0 && (
        <Empty
          Icon={BotIcon}
          title="Aucun agent enregistré"
          description="Le système d'agents n'a pas encore tourné. Lancez le premier cron pour démarrer."
        />
      )}

      {agentsQuery.data && agentsQuery.data.length > 0 && (
        <div className="space-y-2">
          {agentsQuery.data.map((agent) => (
            <AgentRow key={agent.name} agent={agent} />
          ))}
        </div>
      )}
    </>
  );
}

type Agent = Awaited<ReturnType<typeof api.listAgents>>[number];

function AgentRow({ agent }: { agent: Agent }) {
  return (
    <div className="bg-white border border-ducens-border rounded-md px-4 py-3 grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] items-center gap-4 text-sm">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <CircuitDot state={agent.circuit_state} />
          <div className="font-medium truncate">{agent.name}</div>
        </div>
        <div className="text-xs text-ducens-muted mt-0.5">{agent.description}</div>
      </div>

      <div className="text-xs text-ducens-muted whitespace-nowrap">
        <Clock className="w-3 h-3 inline mr-1" />
        {agent.last_run_at
          ? formatDistanceToNow(new Date(agent.last_run_at), { locale: fr, addSuffix: true })
          : 'jamais'}
      </div>

      <div className="text-xs text-ducens-muted whitespace-nowrap">
        {agent.runs_24h} runs · {agent.success_rate_24h.toFixed(0)}% OK
      </div>

      <div>
        {agent.last_run_status === 'success' && <CheckCircle className="w-4 h-4 text-ducens-success" />}
        {agent.last_run_status === 'error' && <XCircle className="w-4 h-4 text-ducens-danger" />}
        {agent.last_run_status === 'timeout' && <XCircle className="w-4 h-4 text-ducens-warn" />}
        {agent.last_run_status === 'skipped' && <span className="text-xs text-ducens-muted">skip</span>}
      </div>
    </div>
  );
}

function CircuitDot({ state }: { state: 'closed' | 'open' | 'half_open' }) {
  return (
    <span
      title={`Circuit ${state}`}
      className={cn(
        'inline-block w-2 h-2 rounded-full',
        state === 'closed' && 'bg-ducens-success',
        state === 'open' && 'bg-ducens-danger',
        state === 'half_open' && 'bg-ducens-warn'
      )}
    />
  );
}
